import React, { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PartyPopper, Star, Heart, Loader2, MessageSquareHeart } from "lucide-react";

const NAME_CHOICES = [
  { v: "first_name", label: "First name only" },
  { v: "initials", label: "Initials only" },
  { v: "anonymous", label: "Anonymous" },
];

export default function CompletionCelebration({ onDone }) {
  const fired = useRef(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rating: 0, text: "", permission_public: false, name_display: "first_name", city: "", state: "",
  });

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    // A single, gentle, muted burst — warm, not flashy.
    const t = setTimeout(() => {
      confetti({
        particleCount: 60, spread: 70, startVelocity: 32, gravity: 0.9, scalar: 0.9,
        ticks: 140, origin: { y: 0.25 },
        colors: ["#1C4358", "#9C1E22", "#E8C37E", "#6FA8A0", "#F4F8FB"],
        disableForReducedMotion: true,
      });
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const later = async () => {
    try { await api.post("/portal/plan-celebration/ack"); } catch { /* best effort */ }
    onDone?.();
  };

  const submit = async () => {
    if (!form.text.trim()) { toast.error("Please share a few words first."); return; }
    setSaving(true);
    try {
      await api.post("/portal/testimonials", {
        rating: form.rating || null, text: form.text.trim(),
        permission_public: form.permission_public, name_display: form.name_display,
        city: form.city.trim() || null, state: form.state.trim() || null,
      });
      toast.success("Thank you — your note has been sent to our team for review.");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e, "Could not send your note.")); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Card
        className="p-6 sm:p-8 border-0 text-white overflow-hidden relative"
        style={{ background: "linear-gradient(135deg,#2F7D5A 0%,#1C4358 100%)" }}
        data-testid="completion-celebration"
      >
        <Heart size={140} className="absolute -right-6 -bottom-8 opacity-10" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-white/15 rounded-full px-3 py-1">
            <PartyPopper size={13} /> A milestone worth marking
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3" data-testid="completion-title">
            Your family transition plan is complete.
          </h2>
          <p className="text-[15px] text-white/85 mt-2">
            You&apos;ve completed the visible next steps in your MovingMyMama plan. Thank you for trusting us to
            walk with your family through this transition.
          </p>

          <div className="mt-5 rounded-xl bg-white/10 p-4">
            <div className="text-[15px] font-medium flex items-center gap-2">
              <MessageSquareHeart size={17} /> Would you like to share how MovingMyMama helped your family?
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button onClick={() => setOpen(true)} data-testid="celebration-share-feedback-btn"
                className="bg-white text-[#1C4358] hover:bg-white/90 font-semibold h-11 rounded-full px-5">
                Share feedback
              </Button>
              <Button onClick={later} variant="ghost" data-testid="celebration-maybe-later-btn"
                className="text-white hover:bg-white/15 h-11 rounded-full px-5">
                Maybe later
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#252221]">Share your experience</DialogTitle>
            <DialogDescription>Your note goes to our team for review before it&apos;s ever shared publicly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <Label className="text-[13px] text-[#4B5563]">How was your experience? (optional)</Label>
              <div className="flex gap-1 mt-1.5" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n === form.rating ? 0 : n })}
                    data-testid={`feedback-star-${n}`} className="p-0.5">
                    <Star size={26} className={n <= form.rating ? "fill-[#E0A526] text-[#E0A526]" : "text-[#D6E0E7]"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[13px] text-[#4B5563]">Your words</Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Tell us how we helped your family…" rows={4}
                className="mt-1 text-[15px]" data-testid="feedback-text" />
            </div>
            <div>
              <Label className="text-[13px] text-[#4B5563]">How should we show your name if we share this?</Label>
              <RadioGroup value={form.name_display} onValueChange={(v) => setForm({ ...form, name_display: v })}
                className="mt-1.5 space-y-1.5" data-testid="feedback-name-display">
                {NAME_CHOICES.map((c) => (
                  <div key={c.v} className="flex items-center gap-2">
                    <RadioGroupItem value={c.v} id={`nd-${c.v}`} data-testid={`feedback-name-${c.v}`} />
                    <Label htmlFor={`nd-${c.v}`} className="text-[14px] font-normal text-[#252221] cursor-pointer">{c.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[13px] text-[#4B5563]">City (optional)</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-10 text-[14px]" data-testid="feedback-city" /></div>
              <div><Label className="text-[13px] text-[#4B5563]">State (optional)</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1 h-10 text-[14px]" data-testid="feedback-state" /></div>
            </div>
            <label className="flex items-start gap-2.5 rounded-lg bg-[#F4F8FB] p-3 cursor-pointer">
              <Checkbox checked={form.permission_public} onCheckedChange={(v) => setForm({ ...form, permission_public: !!v })}
                className="mt-0.5" data-testid="feedback-permission" />
              <span className="text-[13px] text-[#4B5563]">MovingMyMama may use this testimonial publicly. Nothing is shared until our team reviews it.</span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 rounded-full px-5" data-testid="feedback-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white h-11 rounded-full px-6 gap-1.5" data-testid="feedback-submit-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <MessageSquareHeart size={16} />} Send feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
